import { useEffect, useState, type ReactNode } from "react";

import type { SourceRef } from "@automatry/schemas";

import type { ThemeName } from "../app/types";
import type {
  WorkspaceSectionId,
  WorkspaceItem,
  WorkspacePointUse
} from "../viewmodel/solutionViewAdapter";
import type { ScopeMatrixDisplayRow } from "../viewmodel/scopeMatrixAdapter";
import { cx, humaniseIdentifierText, ruleTraceDisplayLabel } from "../app/format";
import {
  TechnicalDetails,
  type TechnicalDetailsEntry
} from "../components/TechnicalDetails";

export { cx, labelFromId } from "../app/format";

export const pointTone = {
  controller: "#4F6B8F",
  heating: "#6E8A4C",
  loads: "#A8823E",
  network: "#6A5E97",
  risk: "#BC4A3C",
  neutral: "var(--muted2)"
} as const;

export const itemTone = {
  Controllers: "#4F6B8F",
  Products: "#A8823E",
  Extensions: "#B08A3E"
} as const;

export const sectionTone: Record<WorkspaceSectionId, string> = {
  controllers: "#4F6B8F",
  "room-ui": "#6A5E97",
  sensors: "#6E8A4C",
  "relays-panel": "#A8823E",
  "edge-leak": "#32745E",
  services: "#3E6E9A",
  other: "var(--muted2)"
};

export const riskTone = {
  none: "border-[var(--success-border)] bg-[var(--success-bg)] text-[var(--ink2)]",
  info: "border-[var(--info-border)] bg-[var(--info-bg)] text-[var(--info)]",
  warning: "border-[var(--warn-border)] bg-[var(--warn-bg)] text-[var(--warn)]",
  blocker: "border-[var(--risk-border)] bg-[var(--risk-bg)] text-[var(--risk)]"
} as const;

export const validationTone = {
  accepted: "border-[#D9DED5] bg-[#EAF1E6] text-[#4E7644]",
  review: "border-[#E9D3AD] bg-[#F6EEDD] text-[#8A6A2E]",
  error: "border-[#E9C7C0] bg-[#F6E7E4] text-[#BC4A3C]",
  info: "border-[var(--line3)] bg-[var(--chip)] text-[var(--muted)]"
} as const;

export const sourceTone = (source: WorkspaceItem["source"]): string => {
  if (source === "Fabricated") {
    return "bg-[#EFE6DA] text-[var(--warn)]";
  }

  if (source === "In-house") {
    return "bg-[#E5F0EC] text-[var(--inhouse)]";
  }

  if (source === "3rd-party") {
    return "bg-[var(--chip)] text-[var(--muted)]";
  }

  return "bg-[#ECEFF3] text-[var(--ink2)]";
};

export const sourceDisplayLabel = (source: WorkspaceItem["source"]): string =>
  source === "In-house" ? "Automatry Edge" : source;

export const formatPoints = (point: WorkspacePointUse): string =>
  `${point.required}/${point.available}`;

export interface ProvenanceEvidence {
  sourceRefs?: readonly SourceRef[];
  sourceRefCount?: number;
  ruleTraceRefs?: readonly string[];
  ruleTraceRefCount?: number;
  confidence?: number;
  confidenceLabel?: string;
}

export const NO_TRIGGER_DEFAULT_LABEL =
  "Default - no specific requirement found in the documents";

const evidenceWhereLabel = (
  sourceRefCount: number,
  ruleTraceRefCount: number
): string => {
  const parts = [
    sourceRefCount > 0
      ? `${sourceRefCount} source${sourceRefCount === 1 ? "" : "s"}`
      : undefined,
    ruleTraceRefCount > 0
      ? `${ruleTraceRefCount} check${ruleTraceRefCount === 1 ? "" : "s"}`
      : undefined
  ].filter((part): part is string => part !== undefined);

  return parts.length > 0
    ? `Where: ${parts.join(" and ")} attached to this row.`
    : "Where: No source trail is available in this view.";
};

export function AiProvenanceBadge({
  why,
  where,
  defaulted = false,
  compact = false,
  className,
  ariaHidden = false
}: {
  why?: ReactNode;
  where?: ReactNode;
  defaulted?: boolean;
  compact?: boolean;
  className?: string;
  ariaHidden?: boolean;
}) {
  const whyNode = why ?? (defaulted ? NO_TRIGGER_DEFAULT_LABEL : "AI-produced");
  const whereNode =
    where ??
    (defaulted
      ? "Where: no matching source requirement is attached to this default."
      : "Where: source trail is attached in evidence.");
  const title =
    typeof whyNode === "string" && typeof whereNode === "string"
      ? `AI-produced. Why: ${whyNode}. ${whereNode}`
      : "AI-produced decision with WHY and WHERE trail.";

  if (compact) {
    return (
      <span
        title={title}
        aria-hidden={ariaHidden ? true : undefined}
        aria-label={ariaHidden ? undefined : title}
        className={cx(
          "inline-flex max-w-full items-center gap-1 rounded-full border border-[#D7E1EA] bg-[#F6FAFD] px-2 py-1 text-[10px] font-extrabold uppercase tracking-[.04em] text-[#3E6E9A]",
          className
        )}
      >
        AI
      </span>
    );
  }

  return (
    <span
      title={title}
      aria-hidden={ariaHidden ? true : undefined}
      className={cx(
        "inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-1 rounded-[9px] border border-[#D7E1EA] bg-[#F6FAFD] px-2.5 py-1.5 text-[10.5px] font-bold leading-[1.35] text-[#3E6E9A]",
        className
      )}
    >
      <span className="font-extrabold uppercase tracking-[.04em]">AI-produced</span>
      <span className="text-[var(--muted)]">Why: {whyNode}</span>
      <span className="text-[var(--muted)]">{whereNode}</span>
    </span>
  );
}

const sourceRefDocumentLabel = (sourceRef: SourceRef): string =>
  humaniseIdentifierText(sourceRef.sourceId.replaceAll("-", " "));

const sourceRefMeta = (sourceRef: SourceRef): string =>
  [
    humaniseIdentifierText(sourceRef.sourceKind),
    humaniseIdentifierText(sourceRef.authorityClass),
    sourceRef.sheetName,
    sourceRef.cellRef,
    sourceRef.rangeRef
  ]
    .filter((value): value is string => typeof value === "string" && value.length > 0)
    .join(" · ");

export function ProvenanceAffordance({
  evidence,
  label = "Evidence",
  compact = false,
  formatRuleTraceRef,
  why,
  where,
  rawData,
  defaulted = false,
  showAiBadge = true
}: {
  evidence: ProvenanceEvidence;
  label?: string;
  compact?: boolean;
  formatRuleTraceRef?: (ruleTraceRef: string) => string;
  why?: ReactNode;
  where?: ReactNode;
  rawData?: unknown;
  defaulted?: boolean;
  showAiBadge?: boolean;
}) {
  const sourceRefs = evidence.sourceRefs ?? [];
  const sourceRefCount = evidence.sourceRefCount ?? sourceRefs.length;
  const ruleTraceRefs = evidence.ruleTraceRefs ?? [];
  const ruleTraceRefCount = evidence.ruleTraceRefCount ?? ruleTraceRefs.length;
  const hasConfidence = typeof evidence.confidence === "number";
  const confidencePercent = hasConfidence
    ? Math.round((evidence.confidence ?? 0) * 100)
    : null;
  const hasEvidence = sourceRefCount > 0 || ruleTraceRefCount > 0 || hasConfidence;
  const displayedSourceCount = Math.min(sourceRefs.length, 4);
  const displayedRuleCount = Math.min(ruleTraceRefs.length, 6);
  const hiddenSourceCount = Math.max(0, sourceRefCount - displayedSourceCount);
  const hiddenRuleCount = Math.max(0, ruleTraceRefCount - displayedRuleCount);
  const ruleTraceLabel = formatRuleTraceRef ?? ruleTraceDisplayLabel;
  const hasRawRefs = sourceRefs.length > 0 || ruleTraceRefs.length > 0;
  const sources = [
    ...sourceRefs.slice(0, 4).map(
      (sourceRef): TechnicalDetailsEntry => ({
        label: sourceRefDocumentLabel(sourceRef),
        detail: humaniseIdentifierText(sourceRef.locator),
        meta: sourceRefMeta(sourceRef)
      })
    ),
    ...(sourceRefCount > sourceRefs.length
      ? [
          {
            label: `${sourceRefCount} source links retained`,
            detail:
              "The locator payload is counted in this view and available in raw data.",
            tone: "info" as const
          }
        ]
      : []),
    ...(hiddenSourceCount > 0
      ? [
          {
            label: `${hiddenSourceCount} more source links`,
            detail: "Open raw data for the complete source reference list.",
            tone: "info" as const
          }
        ]
      : [])
  ];
  const checks = [
    ...(hasConfidence && confidencePercent !== null
      ? [
          {
            label: evidence.confidenceLabel ?? `${confidencePercent}% confidence`,
            tone:
              evidence.confidence !== undefined && evidence.confidence >= 0.85
                ? ("success" as const)
                : evidence.confidence !== undefined && evidence.confidence >= 0.7
                  ? ("warning" as const)
                  : ("risk" as const)
          }
        ]
      : []),
    ...ruleTraceRefs.slice(0, 6).map(
      (ruleTraceRef): TechnicalDetailsEntry => ({
        label: ruleTraceLabel(ruleTraceRef),
        detail: "Design check retained with this item."
      })
    ),
    ...(hiddenRuleCount > 0
      ? [
          {
            label: `${hiddenRuleCount} more checks`,
            detail: "Open raw data for the complete check reference list.",
            tone: "info" as const
          }
        ]
      : [])
  ];

  const provenanceWhere =
    where ?? evidenceWhereLabel(sourceRefCount, ruleTraceRefCount);

  return (
    <span
      className={cx(
        "inline-flex max-w-full flex-wrap items-center gap-2",
        compact ? "align-middle" : "w-full"
      )}
    >
      {showAiBadge ? (
        <AiProvenanceBadge
          why={why}
          where={provenanceWhere}
          defaulted={defaulted}
          compact={compact}
        />
      ) : null}
      <TechnicalDetails
        sources={sources}
        checks={checks}
        rawData={
          hasRawRefs || rawData !== undefined
            ? {
                label,
                sourceRefs,
                sourceRefCount,
                ruleTraceRefs,
                ruleTraceRefCount,
                ...(rawData !== undefined ? { context: rawData } : {})
              }
            : undefined
        }
        showSummaryHelper={!compact}
        className={cx(compact ? "inline-block" : "block", !hasEvidence && "opacity-95")}
      />
    </span>
  );
}

export function ThemeControl({
  theme,
  onTheme
}: {
  theme: ThemeName;
  onTheme: (theme: ThemeName) => void;
}) {
  return (
    <div className="flex rounded-[10px] border border-[var(--line)] bg-[var(--surface)] p-1">
      {(["light", "dark"] as const).map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onTheme(option)}
          className={cx(
            "cursor-pointer rounded-[8px] px-3 py-1.5 text-[11px] font-bold capitalize",
            theme === option
              ? "bg-[var(--ink)] text-[var(--onSolid)]"
              : "text-[var(--muted)] hover:bg-[var(--chip)]"
          )}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

export const scopeStatusTone = {
  included: {
    label: "Included",
    className: "bg-[#EAF1E6] text-[#4E7644]"
  },
  by_others: {
    label: "By others",
    className: "bg-[#ECEFF3] text-[#5E6E82]"
  },
  excluded: {
    label: "Excluded",
    className: "bg-[var(--line2)] text-[var(--muted2)]"
  }
} as const;

export const scopeSourceTone: Record<
  ScopeMatrixDisplayRow["sourceLinkStatus"],
  string
> = {
  available: "bg-[#EAF1E6] text-[#4E7644]",
  not_produced: "bg-[#F6EEDD] text-[#8A6A2E]"
};

export const previewTone = {
  passed: "border-[#D9DED5] bg-[#EAF1E6] text-[#4E7644]",
  warning: "border-[#E9D3AD] bg-[#F6EEDD] text-[#8A6A2E]",
  blocked: "border-[#E9C7C0] bg-[#F6E7E4] text-[#BC4A3C]"
} as const;

export type ProjectLoadingSurface =
  | "project"
  | "requirements"
  | "quote"
  | "products"
  | "decisions";

type ProjectLoadingCopy = {
  label: string;
  title: string;
  detail: string;
  rail: string;
  timeoutTitle?: string;
  timeoutDetail?: string;
};

const projectLoadingCopy: Record<ProjectLoadingSurface, ProjectLoadingCopy> = {
  project: {
    label: "Project",
    title: "Loading project...",
    detail: "Reading saved project data and preparing the workspace.",
    rail: "Loading project"
  },
  requirements: {
    label: "Requirements",
    title: "Loading project...",
    detail: "Preparing the requirements step.",
    rail: "Loading requirements",
    timeoutTitle: "Still working on requirements…",
    timeoutDetail: "Retry, or return to the viewer while this finishes."
  },
  quote: {
    label: "Quote",
    title: "Preparing quote...",
    detail: "Loading priced totals, blockers and line detail.",
    rail: "Loading quote route"
  },
  products: {
    label: "Products",
    title: "Loading product schedule...",
    detail: "Preparing grouped products and export actions.",
    rail: "Loading products route"
  },
  decisions: {
    label: "Decisions",
    title: "Loading decisions...",
    detail: "Opening follow-ups, options and decision context.",
    rail: "Loading decisions route"
  }
};

export function ProjectLoadingScreen({
  error,
  surface = "project",
  onRetry,
  onBackToViewer
}: {
  error: string | null;
  surface?: ProjectLoadingSurface;
  onRetry?: () => void;
  onBackToViewer?: () => void;
}) {
  const copy = projectLoadingCopy[surface];
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setTimedOut(false);

    if (error || surface !== "requirements") {
      return;
    }

    const timeoutId = globalThis.setTimeout(() => setTimedOut(true), 10_000);

    return () => globalThis.clearTimeout(timeoutId);
  }, [error, surface]);

  const showRecovery = !error && surface === "requirements" && timedOut;

  return (
    <div className="flex flex-1 items-center justify-center px-8 py-12">
      <section
        aria-busy={error ? undefined : true}
        className="w-full max-w-[520px] rounded-[16px] border border-[var(--line)] bg-[var(--surface)] p-6"
      >
        <div className="text-[10.5px] font-bold uppercase tracking-[.12em] text-[var(--faint)]">
          {copy.label}
        </div>
        <h1 className="m-0 mt-2 text-[24px] font-extrabold tracking-[-.03em]">
          {showRecovery ? (copy.timeoutTitle ?? copy.title) : copy.title}
        </h1>
        <p className="m-0 mt-2 text-[12.5px] font-semibold leading-[1.5] text-[var(--muted)]">
          {showRecovery ? (copy.timeoutDetail ?? copy.detail) : copy.detail}
        </p>
        {error ? (
          <div className="mt-4 rounded-xl border border-[#E9C7C0] bg-[#F6E7E4] px-4 py-3 text-[12.5px] font-semibold text-[#BC4A3C]">
            Could not load project: {error}
          </div>
        ) : showRecovery ? (
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onRetry ?? (() => globalThis.location.reload())}
              className="min-h-10 cursor-pointer rounded-[8px] bg-[var(--ink)] px-4 py-2.5 text-[12px] font-extrabold text-[var(--onSolid)] transition-colors hover:bg-[var(--ink2)]"
            >
              Retry
            </button>
            {onBackToViewer ? (
              <button
                type="button"
                onClick={onBackToViewer}
                className="min-h-10 cursor-pointer rounded-[8px] border border-[var(--line3)] bg-[var(--surface)] px-4 py-2.5 text-[12px] font-extrabold text-[var(--ink)] transition-colors hover:bg-[var(--chip)]"
              >
                Back to Viewer
              </button>
            ) : null}
          </div>
        ) : surface === "requirements" ? null : (
          <div
            role="progressbar"
            aria-label={copy.rail}
            className="mt-5 h-2 overflow-hidden rounded-full bg-[var(--line2)]"
          >
            <div className="h-full w-1/2 animate-tcsLoading rounded-full bg-[var(--ink)]" />
          </div>
        )}
      </section>
    </div>
  );
}

export function SpecWorkspaceLoadingScreen({ error }: { error: string | null }) {
  if (error) {
    return <ProjectLoadingScreen error={error} surface="products" />;
  }

  const tabs = [
    "Connections",
    "Points & ports",
    "Products",
    "Floor plan",
    "Panel make-up",
    "Schedules"
  ];

  return (
    <div
      aria-busy="true"
      data-testid="spec-workspace-loading-shell"
      className="flex min-h-0 flex-1 flex-col"
    >
      <div className="px-5 pt-2 sm:px-7 lg:px-9">
        <div className="flex min-h-10 items-center gap-2 rounded-lg border border-[#E3C4B8] bg-[#FFF7F2] px-3 py-1.5">
          <span className="h-2 w-2 rounded-full bg-[#A87518]" />
          <span className="text-[12px] font-extrabold text-[var(--ink)]">
            Loading issue readiness
          </span>
          <span className="ml-auto text-[11.5px] font-bold text-[var(--muted)]">
            Checking saved project data...
          </span>
        </div>
      </div>
      <header className="px-5 pt-3 sm:px-7 lg:px-9 lg:pt-4">
        <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
          Apartment type
        </div>
        <div className="mt-1 h-7 w-56 animate-pulse rounded-md bg-[var(--line2)]" />
        <div className="mt-3">
          <div className="mb-1.5 text-[9.5px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
            Property take-offs · loading this type
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Array.from({ length: 5 }, (_, index) => (
              <div
                key={index}
                className="h-[47px] w-[92px] animate-pulse rounded-lg border border-[var(--line)] bg-[var(--surface)]"
              />
            ))}
          </div>
        </div>
        <div
          role="tablist"
          aria-label="Specification result views"
          className="mt-3 flex gap-0.5 overflow-x-auto border-b border-[var(--line)]"
        >
          {tabs.map((tab, index) => (
            <span
              key={tab}
              role="tab"
              aria-selected={index === 0}
              className={`mr-[22px] flex min-h-10 items-center whitespace-nowrap border-b-2 px-1 py-2 text-[13px] font-bold ${
                index === 0
                  ? "mb-[-1px] border-[var(--ink)] text-[var(--ink)]"
                  : "border-transparent text-[var(--faint)]"
              }`}
            >
              {tab}
            </span>
          ))}
        </div>
      </header>
      <div className="flex-1 px-5 py-4 sm:px-7 lg:px-9 lg:py-5">
        <div className="max-w-[760px]">
          <div className="h-3 w-24 animate-pulse rounded bg-[var(--line2)]" />
          <div className="mt-2 h-6 w-64 animate-pulse rounded bg-[var(--line2)]" />
          <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-[var(--line2)]">
            <span className="block h-full w-1/3 animate-tcsLoading rounded-full bg-[#4F6B8F]" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function DataGapPanel({ gaps }: { gaps: readonly string[] }) {
  if (gaps.length === 0) {
    return null;
  }

  return (
    <section className="rounded-[14px] border border-[#E9D3AD] bg-[#F6EEDD] p-4">
      <div className="text-[10.5px] font-bold uppercase tracking-[.1em] text-[#8A6A2E]">
        Some optional detail is not available for this project
      </div>
      <div className="mt-3 grid gap-2">
        {gaps.map((gap) => (
          <div key={gap} className="text-[12.5px] leading-[1.45] text-[var(--ink2)]">
            {gap}
          </div>
        ))}
      </div>
    </section>
  );
}
